import Link from "next/link";
import {
  Hash,
  MapPin,
  FileCog,
  ScanSearch,
  Swords,
  HardDrive,
  Replace,
  ListOrdered,
  ArrowUpRight,
} from "lucide-react";

/** Registry of the public, free tools — the single source for cross-linking. */
export const FREE_TOOLS = [
  {
    key: "joaat",
    href: "/free/joaat",
    label: "JOAAT Hash Generator",
    blurb: "Convert any name to its GetHashKey value.",
    icon: Hash,
  },
  {
    key: "vector3",
    href: "/free/vector3",
    label: "Coordinates Helper",
    blurb: "Reformat GTA5 coords as vector3, vec3 & more.",
    icon: MapPin,
  },
  {
    key: "server-cfg",
    href: "/free/server-cfg",
    label: "server.cfg Builder",
    blurb: "Build a config whose ensure block is in real dependency order.",
    icon: FileCog,
  },
  {
    key: "spawn-index",
    href: "/free/spawn-index",
    label: "Spawn Code Finder",
    blurb: "Every spawn code in a pack, and how we know each one.",
    icon: ScanSearch,
  },
  {
    key: "clash-finder",
    href: "/free/clash-finder",
    label: "Asset Clash Finder",
    blurb: "Which resources are fighting over a file name, and which copy wins.",
    icon: Swords,
  },
  {
    key: "stream-budget",
    href: "/free/stream-budget",
    label: "Stream Budget",
    blurb: "What a joining player downloads, and how much of it is wasted.",
    icon: HardDrive,
  },
  {
    key: "spawn-rename",
    href: "/free/spawn-rename",
    label: "Spawn Code Renamer",
    blurb: "Rename a vehicle everywhere its pack actually references it.",
    icon: Replace,
  },
  {
    key: "id-bench",
    href: "/free/id-bench",
    label: "ID Bench",
    blurb: "Mod kit, siren and light ids that collide — and a safe renumber.",
    icon: ListOrdered,
  },
] as const;

export type FreeToolKey = (typeof FREE_TOOLS)[number]["key"];

/**
 * "More free tools" cross-link grid. Server component. Omits the current tool
 * when `current` is supplied so every free page links to the others.
 */
export function FreeToolsNav({ current }: { current?: FreeToolKey }) {
  const tools = FREE_TOOLS.filter((t) => t.key !== current);
  // Every tool page passes `current`; the `/free` index is the one caller that
  // omits it, and there this grid IS the page's tool list. So no link to the
  // index from the index, and a tighter gap under its short header.
  const isIndex = current === undefined;
  // "More" is relative to the tool you are on; the index is on no tool.
  const heading = isIndex ? "All free tools" : "More free tools";
  return (
    <section aria-label={heading} className={isIndex ? "mt-10" : "mt-16"}>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {heading}
        </h2>
        {isIndex ? null : (
          <Link
            href="/free"
            className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            All free tools
          </Link>
        )}
      </div>
      {/*
        Column count is a function of how many cards actually render, which is
        FREE_TOOLS minus the current tool: nine registered tools means eight
        cards on every tool page and nine on the `/free` index. At the `xl`
        viewport the wide pages run to `max-w-7xl`, where three cards leave half
        the row empty, so a tool page's ladder ends at four: 8 → 4+4. Four was
        already the count the rest of the site's card grids use. The index stops
        at three instead, so its nine cards are 3+3+3 rather than 4+4+1 with an
        orphan. Below `xl` the prose pages are clamped to `max-w-3xl` and three
        is as many as fits without the blurb wrapping to four lines.
      */}
      <div
        className={`grid gap-3 sm:grid-cols-2 lg:grid-cols-3 ${isIndex ? "" : "xl:grid-cols-4"}`}
      >
        {tools.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.key}
              href={t.href}
              className="group flex items-start gap-3 rounded-xl border border-border/60 bg-card p-4 transition-colors hover:border-primary/30 hover:bg-muted/40"
            >
              <span className="mt-0.5 text-primary/80">
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                  {t.label}
                  <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-primary" />
                </span>
                <span className="mt-1 block text-xs leading-5 text-muted-foreground">
                  {t.blurb}
                </span>
              </span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
